import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { WHBABTEC_STORAGE } from '@whbabtec-types';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  WHBABTEC_DEFAULT_LANG,
  WHBABTEC_LANGS,
  WhbabtecLanguages,
} from '../types/languages.type';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private currentLanguage$$ = new BehaviorSubject<
    WhbabtecLanguages | undefined
  >(undefined);

  constructor(private router: Router, private storageService: StorageService) {
    const stored = this.storageService.get<WhbabtecLanguages>(
      WHBABTEC_STORAGE.LANGUAGE
    );
    const fromUrl = this.router.url.split('/')[1] as WhbabtecLanguages;
    const candidate = stored || fromUrl;
    const lang = WHBABTEC_LANGS.includes(candidate)
      ? candidate
      : WHBABTEC_DEFAULT_LANG;
    this.storeLanguage(lang);
    this.currentLanguage$$.next(lang);
  }

  get currentLanguage$(): Observable<WhbabtecLanguages | undefined> {
    return this.currentLanguage$$.asObservable();
  }

  public setLanguage(lang: WhbabtecLanguages): void {
    const navigationUrl = this.router.url
      .split('/')
      .map((slug) =>
        slug.replace(this.currentLanguage$$.getValue() as string, lang)
      )
      .join('/');

    this.storeLanguage(lang);

    this.currentLanguage$$.next(lang);
    this.router.navigate([navigationUrl]);
  }

  public syncLanguage(lang: WhbabtecLanguages): void {
    this.storeLanguage(lang);
    this.currentLanguage$$.next(lang);
  }

  private storeLanguage(lang: WhbabtecLanguages): void {
    this.storageService.set<string>(WHBABTEC_STORAGE.LANGUAGE, lang);
  }

  /**
   * Returns the short language code (e.g. 'de', 'en') from the current language.
   * Used for OAuth ui_locales parameter.
   */
  public getMappedLanguage(): string {
    const lang = this.currentLanguage$$.getValue() || 'en_global';
    return lang.split('_')[0];
  }

  public getCurrentLanguage(): WhbabtecLanguages {
    return this.currentLanguage$$.getValue() || 'en_global';
  }
}
